/**
 * North Star's "Facilities per state" map (V4 nsDraw, L30238): US states (Albers USA) shaded
 * by how many catalog facilities each holds, Texas and Florida outlined blue as the starting
 * states, a legend, a hover tip, and a click that selects a state and zooms to it. d3-geo,
 * topojson-client and the us-atlas states are bundled (the design loads them from a CDN);
 * the states file is its own chunk, loaded when the map opens.
 *
 * Final attributes are set directly, never only inside a transition.
 */
import { useEffect, useRef } from 'react';
import { geoAlbersUsa, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import { feature } from 'topojson-client';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { sx } from '../../../ui/designStyles';

/** State names as us-atlas spells them, by postal code (V4 NS_NAMES). */
export const NS_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};
const AB: Record<string, string> = Object.fromEntries(Object.entries(NS_NAMES).map(([k, v]) => [v, k]));
/** The starting states, outlined on the map. */
export const STARTING = new Set(['TX', 'FL']);
const OUTLINE = '#4F8AD6';

type StateFeature = Feature<Geometry, { name: string }>;
type Topo = { objects: { states: unknown } };
let topoPromise: Promise<Topo> | null = null;
const loadTopo = () => (topoPromise ??= import('us-atlas/states-10m.json').then((m) => ((m as { default?: Topo }).default ?? m) as Topo));

/** d3.interpolateRgb between two hex colours, formatted as d3 formats it. */
function lerpRgb(a: string, b: string, t: number): string {
  const p = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [x, y] = [p(a), p(b)];
  const c = x.map((v, i) => Math.max(0, Math.min(255, Math.round(v + (y[i] - v) * t))));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

const MSG = (t: string) => `<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:16px;color:#54606C;">${t}</div>`;

interface Host extends HTMLDivElement {
  __feats?: StateFeature[];
  __w?: number;
  __h?: number;
  __sig?: string;
}

export function NsMap({ counts, sel, onPick, style }: { counts: Record<string, number>; sel: string | null; onPick: (st: string) => void; style: string }) {
  const ref = useRef<Host>(null);
  const pick = useRef(onPick);
  pick.current = onPick;
  const selRef = useRef(sel);
  selRef.current = sel;
  const sig = JSON.stringify(counts);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let live = true;
    const draw = (topo: Topo) => {
      if (!live) return;
      const W = el.clientWidth || 600;
      const H = el.clientHeight || 400;
      const max = Math.max(1, ...Object.values(counts));
      const shade = (n: number | undefined) => (!n ? '#EFEBE1' : lerpRgb('#DCE7F4', '#24518A', Math.sqrt(n / max)));
      const s = selRef.current;
      if (el.__w !== W || el.__h !== H || el.__sig !== sig || !el.__feats) {
        const feats = (feature(topo as never, (topo.objects as { states: never }).states) as unknown as FeatureCollection<Geometry, { name: string }>).features as StateFeature[];
        const proj = geoAlbersUsa().fitExtent([[16, 16], [W - 16, H - 44]], { type: 'FeatureCollection', features: feats });
        const path = geoPath(proj);
        el.innerHTML = '';
        const svg = select(el).append('svg').attr('width', W).attr('height', H).style('display', 'block');
        const g = svg.append('g').attr('class', 'ns-g');
        const tip = select(el)
          .append('div')
          .style('position', 'absolute')
          .style('pointer-events', 'none')
          .style('display', 'none')
          .style('background', '#22272E')
          .style('color', '#fff')
          .style('font', '600 12px Archivo, sans-serif')
          .style('padding', '6px 9px')
          .style('border-radius', '6px')
          .style('white-space', 'nowrap');
        g.selectAll('path')
          .data(feats)
          .join('path')
          .attr('d', (f) => path(f) ?? '')
          .attr('data-ab', (f) => AB[f.properties.name] || '')
          .attr('fill', (f) => shade(counts[AB[f.properties.name]]))
          .attr('stroke', '#FCFAF4')
          .attr('stroke-width', 1)
          .style('cursor', 'pointer')
          .on('mousemove', function (ev: MouseEvent, f) {
            const ab = AB[f.properties.name];
            const n = counts[ab] || 0;
            const r = el.getBoundingClientRect();
            tip
              .style('display', 'block')
              .style('left', ev.clientX - r.left + 12 + 'px')
              .style('top', ev.clientY - r.top + 12 + 'px')
              .text(f.properties.name + ' · ' + n.toLocaleString('en-US') + ' facilities' + (STARTING.has(ab) ? ' · starting state' : ''));
            select(this).attr('stroke', '#22272E').attr('stroke-width', 1.4).raise();
          })
          .on('mouseleave', function (_ev: MouseEvent, f) {
            tip.style('display', 'none');
            const ab = AB[f.properties.name];
            const on = selRef.current === ab;
            const st0 = STARTING.has(ab);
            select(this)
              .attr('stroke', on ? '#22272E' : st0 ? OUTLINE : '#FCFAF4')
              .attr('stroke-width', on ? 2 : st0 ? 1.6 : 1);
          })
          .on('click', (_ev: MouseEvent, f) => {
            const ab = AB[f.properties.name];
            if (ab) pick.current(ab);
          });
        // legend
        const lg = svg.append('g').attr('transform', 'translate(18,' + (H - 26) + ')');
        [0.02, 0.15, 0.35, 0.65, 1].forEach((x, i) => {
          lg.append('rect').attr('x', i * 34).attr('y', 0).attr('width', 32).attr('height', 9).attr('rx', 2).attr('fill', shade(x * x * max));
        });
        lg.append('text').attr('x', 0).attr('y', 21).attr('font-family', 'Archivo, sans-serif').attr('font-size', 11).attr('fill', '#54606C').text('Fewer');
        lg.append('text').attr('x', 168).attr('y', 21).attr('text-anchor', 'end').attr('font-family', 'Archivo, sans-serif').attr('font-size', 11).attr('fill', '#54606C').text('More facilities');
        lg.append('rect').attr('x', 190).attr('y', -1).attr('width', 14).attr('height', 11).attr('rx', 2).attr('fill', 'none').attr('stroke', OUTLINE).attr('stroke-width', 1.6);
        lg.append('text').attr('x', 210).attr('y', 9).attr('font-family', 'Archivo, sans-serif').attr('font-size', 11).attr('fill', '#54606C').text('Starting state (TX, FL)');
        el.__feats = feats;
        el.__w = W;
        el.__h = H;
        el.__sig = sig;
        (el as unknown as { __path: typeof path }).__path = path;
      }
      const path = (el as unknown as { __path: ReturnType<typeof geoPath> }).__path;
      const g = select(el).select<SVGGElement>('g.ns-g');
      let k = 1;
      let tr = 'translate(0,0) scale(1)';
      if (s) {
        const f = el.__feats!.find((x) => AB[x.properties.name] === s);
        if (f) {
          const b = path.bounds(f);
          const dx = b[1][0] - b[0][0];
          const dy = b[1][1] - b[0][1];
          const cx = (b[0][0] + b[1][0]) / 2;
          const cy = (b[0][1] + b[1][1]) / 2;
          k = Math.max(1, Math.min(6, 0.75 / Math.max(dx / W, dy / (H - 40))));
          tr = 'translate(' + (W / 2 - k * cx) + ',' + ((H - 40) / 2 - k * cy) + ') scale(' + k + ')';
        }
      }
      g.selectAll<SVGPathElement, StateFeature>('path')
        .attr('stroke', function () {
          const ab = this.getAttribute('data-ab') ?? '';
          return s === ab ? '#22272E' : STARTING.has(ab) ? OUTLINE : '#FCFAF4';
        })
        .attr('stroke-width', function () {
          const ab = this.getAttribute('data-ab') ?? '';
          if (s) return (s === ab ? 2 : 1) / k;
          return STARTING.has(ab) ? 1.6 : 1;
        })
        .attr('opacity', function () {
          const ab = this.getAttribute('data-ab');
          return !s || s === ab ? 1 : 0.45;
        });
      g.attr('transform', tr);
    };
    loadTopo()
      .then(draw)
      .catch(() => {
        if (live) el.innerHTML = MSG('Map could not load. The Ranked view still works.');
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, sel]);

  return <div ref={ref} data-testid="intel-map" style={sx('position:relative;min-width:0;overflow:hidden;' + style)} />;
}
